// Cliente HTTP fino sobre fetch, com token bearer.

import type {
  PlayerProfile,
  PlayerProgress,
  StageResult,
  ProgressResponse,
} from '@shared/types'

const BASE = '/api'

function token(): string | null {
  return localStorage.getItem('cs_token')
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`

  const res = await fetch(`${BASE}${path}`, { ...init, headers })
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    const message = data?.error ?? `HTTP ${res.status}`
    throw new ApiError(message, res.status)
  }
  return data as T
}

export const api = {
  // auth
  register(username: string, email: string, password: string) {
    return request<{ ok: boolean }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    })
  },

  login(username: string, password: string) {
    return request<{
      token: string
      user: PlayerProfile
      progress: PlayerProgress
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
  },

  googleLogin(idToken: string) {
    return request<{
      token: string
      user: PlayerProfile
      progress: PlayerProgress
    }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    })
  },

  logout() {
    return request<{ ok: boolean }>('/auth/logout', { method: 'POST' })
  },

  changePassword(username: string, currentPassword: string, newPassword: string) {
    return request<{ ok: boolean }>('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ username, currentPassword, newPassword }),
    })
  },

  // profile / progress
  profile() {
    return request<{
      ok: boolean
      user: PlayerProfile
      progress: PlayerProgress
    }>('/profile')
  },

  progress() {
    return request<PlayerProgress>('/progress')
  },

  saveProgress(result: StageResult) {
    return request<ProgressResponse>('/progress', {
      method: 'POST',
      body: JSON.stringify(result),
    })
  },

  setSkin(skin: 'raiz' | 'dourado' | 'equilibrado' | 'instrutor') {
    return request<{ ok: boolean; skin: string }>('/profile/skin', {
      method: 'POST',
      body: JSON.stringify({ skin }),
    })
  },

  setDifficulty(difficulty: 'easy' | 'medium' | 'hard') {
    return request<{ ok: boolean; difficulty: string }>('/profile/difficulty', {
      method: 'POST',
      body: JSON.stringify({ difficulty }),
    })
  },

  // techniques
  techniques() {
    return request<{
      techniques: Array<{
        slug: string
        name: string
        cordaRequired: string
        axeCost: number
        damage: number
        unlocked: boolean
      }>
    }>('/techniques')
  },

  // survival
  saveSurvivalRecord(data: { waveReached: number; score: number }) {
    return request<{ ok: boolean; xpGained: number; totalXp: number }>('/survival/record', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  getLeaderboard() {
    return request<Array<{ username: string; best_survival_wave: number; best_survival_score: number }>>('/survival/leaderboard')
  },
}
